import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Profile.css';
import instagramIcon from "../../src/assets/logo/instagram.webp";
import twitterIcon from "../../src/assets/logo/twitter.webp";
import youtubeIcon from "../../src/assets/logo/youtube.webp";
import linkedinIcon from "../../src/assets/logo/linkedin.webp";
import facebookIcon from "../../src/assets/logo/facebook.webp";
import websiteIcon from "../../src/assets/logo/website.webp";
import { BACKEND_URL } from "../config";



const SOCIAL_ICON_MAP = {
  Instagram: instagramIcon,
  Twitter: twitterIcon,
  YouTube: youtubeIcon,
  LinkedIn: linkedinIcon,
  Facebook: facebookIcon,
  Website: websiteIcon,
  GitHub: websiteIcon,
  Other: websiteIcon
};

const API = BACKEND_URL;

function authHeader() {
  const t = localStorage.getItem('ihn_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [blogFile, setBlogFile] = useState(null);
  const [step, setStep] = useState(0);
  const [copyMsg, setCopyMsg] = useState('');
  const [companyLogoFile, setCompanyLogoFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [aadharFile, setAadharFile] = useState(null);
  const [gstFile, setGstFile] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    async function load() {
      setLoading(true); setErr('');
      try {
        const res = await axios.get(`${API}/api/users/me`, { headers: authHeader() });
        const data = res.data.profile;
        const arrayFields = [
          'socialLinks', 'personalSocialLinks', 'companySocialLinks',
          'awards', 'blogs', 'gallery', 'officeBranches', 'membershipDetails'
        ];
        arrayFields.forEach(field => {
          if (typeof data[field] === 'string') {
            try {
              data[field] = JSON.parse(data[field]);
            } catch (e) {
              data[field] = []; // Fallback to empty array if parsing fails
            }
          }
          // Ensure it's at least an empty array if it's null/undefined
          if (!data[field]) data[field] = [];
        });
        // Parse socialLinks if it's a string
        if (typeof data.socialLinks === 'string') {
          try { data.socialLinks = JSON.parse(data.socialLinks); } catch (e) { data.socialLinks = []; }
        }

        // Ensure nested objects exist to prevent runtime errors
        if (!data.officeLocation) {
          data.officeLocation = { address: '', pincode: '', state: '', country: '' };
        }

        // Handle custom sector for natureOfBusiness
        const standardSectors = [
          "Airline", "Cruise", "Hotel / Resort", "Travel Agency", "Tour Operator",
          "Destination Management Company", "Online Travel Platform",
          "Transport / Mobility Services", "Events & MICE",
          "Adventure / Experience Tourism", "Wellness / Medical Tourism",
          "Hospitality Services", "Travel Technology"
        ];
        if (data.natureOfBusiness && !standardSectors.includes(data.natureOfBusiness) && data.natureOfBusiness !== 'Other') {
           data.customSector = data.natureOfBusiness;
           data.natureOfBusiness = 'Other';
        }

        setProfile(data);
      } catch (e) {
        setErr(e?.response?.data?.message || 'Unable to load profile');
      } finally { setLoading(false) }
    }
    load();
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    setLoading(true);
    setErr('');

    try {
      const fd = new FormData();

      // === Files ===
      if (photoFile) fd.append('photo', photoFile);
      // if (blogFile) fd.append('blog', blogFile);
      if (companyLogoFile) fd.append('companyLogo', companyLogoFile);
      if (panFile) fd.append('panFile', panFile);
      if (aadharFile) fd.append('aadharFile', aadharFile);
      if (gstFile) fd.append('gstFile', gstFile);

      // === Basic fields ===
      const basicFields = [
        'username', 'name', 'introduction', 'companyIntro', 'website',
        'blogName', 'companyMembers', 'yearStarted', 'companyName',
        'address', 'dob', 'pincode', 'state', 'country', 'annualTurnover'
      ];
      basicFields.forEach(f => fd.append(f, profile[f] || ''));

      // Handle "Other" sector for natureOfBusiness
      let finalNature = profile.natureOfBusiness;
      if (finalNature === 'Other' && profile.customSector) {
        finalNature = profile.customSector;
      }
      fd.append('natureOfBusiness', finalNature || '');

      // Handle boolean for tinyint
      fd.append('isHiring', profile.isHiring ? '1' : '0');

      // === Mapped Fields for Database Compatibility ===
      fd.append('panNumber', profile.panCard || '');
      fd.append('aadharNumber', profile.aadharCard || '');
      fd.append('gstNumber', profile.gst || '');

      // === JSON fields ===
      fd.append('personalSocialLinks', JSON.stringify(profile.personalSocialLinks || []));
      fd.append('companySocialLinks', JSON.stringify(profile.companySocialLinks || []));
      fd.append('officeBranches', JSON.stringify(profile.officeBranches || []));
      fd.append('membershipDetails', JSON.stringify(profile.membershipDetails || []));
      fd.append('officeLocation', JSON.stringify(profile.officeLocation));

      // === Map First Membership Item for Singular Columns ===
      if (profile.membershipDetails && profile.membershipDetails.length > 0) {
        const m = profile.membershipDetails[0];
        fd.append('membershipSociety', m.society || '');
        fd.append('natureOfMembership', m.nature || '');
        fd.append('chosenMembershipPlan', m.plan || '');
        fd.append('membershipPlan', m.plan || '');
      }

      // === Blogs ===
      const blogsData = (profile.blogs || []).map(b => ({
          title: b.title || '',
          image: b.image || '',
          pdf: b.pdf || '',
          link: b.link || '',
          para: b.content || ''
      }));
      fd.append('blogs', JSON.stringify(blogsData));

      // Map first blog to singular columns
      if (blogsData.length > 0) {
        const b = blogsData[0];
        fd.append('blogTitle', b.title);
        fd.append('blogImage', b.image);
        fd.append('blogPdf', b.pdf);
        fd.append('blogLink', b.link);
        fd.append('blogPara', b.para);
      }

      // === Awards ===
      const awardsData = (profile.awards || []).map(a => ({
          title: a.title || '',
          year: a.year || '',
          image: a.image || '',
          otherInfo: a.info || ''
      }));
      fd.append('awards', JSON.stringify(awardsData));

      // Map first award to singular columns
      if (awardsData.length > 0) {
        const a = awardsData[0];
        fd.append('awardTitle', a.title);
        fd.append('awardYear', a.year);
        fd.append('awardImage', a.image);
        fd.append('awardOtherInfo', a.otherInfo);
      }

      // === Gallery ===
      const galleryData = (profile.gallery || []).map(g => ({
        mediaType: g.type || 'photo', // rename type → mediaType
        mediaUrl: g.url || ''
      }));
      fd.append('gallery', JSON.stringify(galleryData));

      // Map first gallery item to singular columns
      if (galleryData.length > 0) {
         const g = galleryData[0];
         if (g.mediaType === 'image' || g.mediaType === 'photo') fd.append('galleryPhoto', g.mediaUrl);
         if (g.mediaType === 'video') fd.append('galleryVideo', g.mediaUrl);
      }

      // Append gallery files as array
      (profile.gallery || []).forEach((g) => {
        if (g.file) fd.append('galleryFiles', g.file);
      });

      // === Send request ===
      const res = await axios.put(`${API}/api/users`, fd, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' }
      });

      // Update profile and regenerate QR
      setProfile(res.data.profile);
      alert('Application submitted successfully! Your profile is under review.');

    } catch (e) {
      setErr(e?.response?.data?.message || 'Unable to save');
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div style={{ padding: '2rem' }}>Loading...</div>;
  if (err) return <div style={{ padding: '2rem', color: 'crimson' }}>{err}</div>;
if (!profile) return <div>Loading...</div>;
  const stepNames = [
    "Personal Info",
    "Company Info",
    "Awards",
    "Blogs",
    "Membership",
    "Business Details"
  ];
  return (
    <div className="profile-wrap">
      <div className="profile-card">
        <h2>Sign-Up</h2>
        <form onSubmit={handleSave} className="profile-form">
          <div className="step-indicator-buttons">
            {stepNames.map((name, idx) => (
              <button
                key={idx}
                className={`profile-tab-btn ${step === idx ? 'active' : ''}`}
                type="button"
                onClick={() => setStep(idx)}
              >
                {name}
              </button>
            ))}
          </div>
          {step === 0 && (
            <section className="slide">
              <h3>Personal Information</h3>

              {/* Row 1 */}
              <div className="form-row two-col">
                <label>
                  <span>Upload Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => setPhotoFile(e.target.files[0])}
                  />
                </label>

                <label>
                  <span>Date of Birth</span>
                  <input
                    type="date"
                    value={profile.dob || ''}
                    onChange={e => setProfile({ ...profile, dob: e.target.value })}
                  />
                </label>
              </div>
              <div className='row'>
                <label>
                  <span>Address</span>
                  <input type='textarea'
                    value={profile.address || ''}
                    onChange={e => setProfile({ ...profile, address: e.target.value })}
                  />
                </label>
              </div>
              {/* Row 2 */}
              <div className="form-row four-col mt-4">


                <label>
                  <span>Pincode</span>
                  <input
                    value={profile.pincode || ''}
                    onChange={e => setProfile({ ...profile, pincode: e.target.value })}
                  />
                </label>

                <label>
                  <span>State</span>
                  <input
                    value={profile.state || ''}
                    onChange={e => setProfile({ ...profile, state: e.target.value })}
                  />
                </label>

                <label>
                  <span>Country</span>
                  <input
                    value={profile.country || ''}
                    onChange={e => setProfile({ ...profile, country: e.target.value })}
                  />
                </label>
              </div>
            </section>
          )}


          {step === 1 && (
            <section className="slide">
              <h3>Company Information</h3>
              <label className='mt-4'><span>Company Name</span>
                <input value={profile.companyName || ''} onChange={e => setProfile({ ...profile, companyName: e.target.value })} />
              </label>
              <div className='form-row mt-4'>
                <label><span>Company Logo</span>
                  <input type="file" accept="image/*" onChange={e => setCompanyLogoFile(e.target.files[0])} />
                </label>
                <label className='mt-4'><span>Website</span>
                  <input value={profile.website || ''} onChange={e => setProfile({ ...profile, website: e.target.value })} />
                </label>
              </div>
              <label className='mt-4'><span>Company Introduction</span>
                <textarea value={profile.companyIntro || ''} onChange={e => setProfile({ ...profile, companyIntro: e.target.value })} />
              </label>
              <div className='form-row'>
                <label className='mt-4'><span>Members in Organisation</span>
                  <select value={profile.companyMembers || ''} onChange={e => setProfile({ ...profile, companyMembers: e.target.value })}>
                    <option value="">Select Range</option>
                    <option value="0-10">0-10</option>
                    <option value="11-20">11-20</option>
                    <option value="21-50">21-50</option>
                    <option value="51-100">51-100</option>
                    <option value="100-500">100-500</option>
                    <option value="500+">500+</option>
                  </select>
                </label>
                <label className='mt-4'><span>Annual Turnover</span>
                  <select value={profile.annualTurnover || ''} onChange={e => setProfile({ ...profile, annualTurnover: e.target.value })}>
                    <option value="">Select Turnover</option>
                    <option value="Less than 1 Cr">Less than 1 Cr</option>
                    <option value="1 Cr - 2.5 Cr">1 Cr - 2.5 Cr</option>
                    <option value="2.5 Cr - 5 Cr">2.5 Cr - 5 Cr</option>
                    <option value="5 Cr - 10 Cr">5 Cr - 10 Cr</option>
                    <option value="10 Cr - 50 Cr">10 Cr - 50 Cr</option>
                    <option value="50 Cr - 100 Cr">50 Cr - 100 Cr</option>
                    <option value="100 Cr+">100 Cr+</option>
                  </select>
                </label>
              </div>
              <div className='form-row'>
                <label className='mt-4'><span>Year of Incorporation</span>
                  <input type="number" value={profile.yearStarted || ''} onChange={e => setProfile({ ...profile, yearStarted: e.target.value })} />
                </label>
                <label className="mt-4">
                  <span>Sector of Business</span>
                  <select
                    value={profile.natureOfBusiness || ""}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        natureOfBusiness: e.target.value,
                        customSector: e.target.value === "Other" ? profile.customSector || "" : ""
                      })
                    }
                  >
                    <option value="">Select Sector</option>

                    <option value="Airline">Airline</option>
                    <option value="Cruise">Cruise</option>
                    <option value="Hotel / Resort">Hotel / Resort</option>
                    <option value="Travel Agency">Travel Agency</option>
                    <option value="Tour Operator">Tour Operator</option>
                    <option value="Destination Management Company">
                      Destination Management Company (DMC)
                    </option>
                    <option value="Online Travel Platform">
                      Online Travel Platform (OTA)
                    </option>
                    <option value="Transport / Mobility Services">
                      Transport / Mobility Services
                    </option>
                    <option value="Events & MICE">Events & MICE</option>
                    <option value="Adventure / Experience Tourism">
                      Adventure / Experience Tourism
                    </option>
                    <option value="Wellness / Medical Tourism">
                      Wellness / Medical Tourism
                    </option>
                    <option value="Hospitality Services">Hospitality Services</option>
                    <option value="Travel Technology">Travel Technology</option>

                    <option value="Other">Other</option>
                  </select>
                </label>
{profile.natureOfBusiness === "Other" && (
  <label className="mt-3">
    <span>Please specify your sector</span>
    <input
      type="text"
      placeholder="Enter your business sector"
      value={profile.customSector || ""}
      onChange={e =>
        setProfile({ ...profile, customSector: e.target.value })
      }
    />
  </label>
)}

              </div>

              <div className=" mt-4">
                <h5 className="profile-card-title">Office Address</h5>

                <input
                  className="profile-input"
                  placeholder="Address"
                  value={profile.officeLocation?.address || ''}
                  onChange={e =>
                    setProfile({
                      ...profile,
                      officeLocation: {
                        ...profile.officeLocation,
                        address: e.target.value
                      }
                    })
                  }
                />

                <div className="form-row four-col mt-2">
                  <input
                    placeholder="Pincode"
                    value={profile.officeLocation?.pincode || ''}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        officeLocation: {
                          ...profile.officeLocation,
                          pincode: e.target.value
                        }
                      })
                    }
                  />

                  <input
                    placeholder="State"
                    value={profile.officeLocation?.state || ''}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        officeLocation: {
                          ...profile.officeLocation,
                          state: e.target.value
                        }
                      })
                    }
                  />

                  <input
                    placeholder="Country"
                    value={profile.officeLocation?.country || ''}
                    onChange={e =>
                      setProfile({
                        ...profile,
                        officeLocation: {
                          ...profile.officeLocation,
                          country: e.target.value
                        }
                      })
                    }
                  />
                </div>
              </div>

              {/* HIRING STATUS */}
              <div className='mt-4'>
                <label className="profile-label">
                  <span>Is your company actively hiring?</span>
                  <select
                    className="profile-select"
                    value={profile.isHiring ? 'yes' : 'no'}
                    onChange={e => {
                      const isHiring = e.target.value === 'yes';
                      setProfile({ ...profile, isHiring });
                    }}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </label>

              </div>

              {/* OFFICE BRANCHES */}
              <div className="mt-4 profile-card">
                <label><span>Office Branches</span></label>


                {(profile.officeBranches || []).map((b, idx) => (
                  <div key={idx} className="profile-branch-box">

                    <input
                      className="profile-input"
                      placeholder="Address"
                      value={b.address}
                      onChange={e => {
                        const copy = [...profile.officeBranches];
                        copy[idx].address = e.target.value;
                        setProfile({ ...profile, officeBranches: copy });
                      }}
                    />

                    <div className="form-row four-col mt-2">
                      <input
                        placeholder="Pincode"
                        value={b.pincode}
                        onChange={e => {
                          const copy = [...profile.officeBranches];
                          copy[idx].pincode = e.target.value;
                          setProfile({ ...profile, officeBranches: copy });
                        }}
                      />

                      <input
                        placeholder="State"
                        value={b.state}
                        onChange={e => {
                          const copy = [...profile.officeBranches];
                          copy[idx].state = e.target.value;
                          setProfile({ ...profile, officeBranches: copy });
                        }}
                      />

                      <input
                        placeholder="Country"
                        value={b.country}
                        onChange={e => {
                          const copy = [...profile.officeBranches];
                          copy[idx].country = e.target.value;
                          setProfile({ ...profile, officeBranches: copy });
                        }}
                      />
                    </div>

                    <button
                      type="button"
                      className="profile-btn-outline profile-danger mt-2"
                      onClick={() => {
                        const copy = [...profile.officeBranches];
                        copy.splice(idx, 1);
                        setProfile({ ...profile, officeBranches: copy });
                      }}
                    >
                      Remove Branch
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="profile-btn-secondary"
                  onClick={() => {
                    const copy = [...(profile.officeBranches || [])];
                    copy.push({ address: "", pincode: "", state: "", country: "" });
                    setProfile({ ...profile, officeBranches: copy });
                  }}
                >
                  + Add Branch
                </button>
              </div>


              {/* SOCIAL MEDIA */}
              <div className="profile-card">
                <label><span>Official Social Media Accounts</span></label>

                {(profile.companySocialLinks || []).map((s, idx) => (
                  <div key={idx} className="profile-inline-row">
                    <select
                      className="profile-select"
                      value={s.platform}
                      onChange={e => {
                        const copy = [...profile.companySocialLinks];
                        copy[idx].platform = e.target.value;
                        setProfile({ ...profile, companySocialLinks: copy });
                      }}
                    >
                      <option value="">Platform</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Twitter">Twitter</option>
                      <option value="Instagram">Instagram</option>
                      <option value="Facebook">Facebook</option>
                      <option value="YouTube">YouTube</option>
                    </select>

                    <input
                      className="profile-input"
                      placeholder="Profile URL"
                      value={s.url}
                      onChange={e => {
                        const copy = [...profile.companySocialLinks];
                        copy[idx].url = e.target.value;
                        setProfile({ ...profile, companySocialLinks: copy });
                      }}
                    />

                    <button
                      type="button"
                      className="profile-btn-outline profile-danger"
                      onClick={() => {
                        const copy = [...profile.companySocialLinks];
                        copy.splice(idx, 1);
                        setProfile({ ...profile, companySocialLinks: copy });
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="profile-btn-secondary"
                  onClick={() => {
                    const copy = [...(profile.companySocialLinks || [])];
                    copy.push({ platform: '', url: '' });
                    setProfile({ ...profile, companySocialLinks: copy });
                  }}
                >
                  + Add Account
                </button>
              </div>

              {/* MEDIA / GALLERY */}
              <div className="profile-card">

                <label><span>Media (Brochure, Images & Reels)</span></label>


                <div className="profile-gallery-grid">
                  {(profile.gallery || []).map((g, idx) => (
                    <div key={idx} className="profile-gallery-card mt-2">

                      <div className="profile-gallery-header">
                        <select
                          className="profile-select"
                          value={g.type || 'image'}
                          onChange={e => {
                            const copy = [...profile.gallery];
                            copy[idx].type = e.target.value;
                            setProfile({ ...profile, gallery: copy });
                          }}
                        >
                          <option value="image">🖼️ Image</option>
                          <option value="video">🎥 Video</option>
                        </select>

                        <select
                          className="profile-select"
                          value={g.uploadMethod || 'link'}
                          onChange={e => {
                            const copy = [...profile.gallery];
                            copy[idx].uploadMethod = e.target.value;
                            setProfile({ ...profile, gallery: copy });
                          }}
                        >
                          <option value="link">Via Link</option>
                          <option value="file">Upload File</option>
                        </select>

                        <button
                          type="button"
                          className="profile-btn-outline profile-danger"
                          onClick={() => {
                            const copy = [...profile.gallery];
                            copy.splice(idx, 1);
                            setProfile({ ...profile, gallery: copy });
                          }}
                        >
                          Remove
                        </button>
                      </div>

                      {g.uploadMethod === 'file' ? (
                        <input
                          type="file"
                          className="profile-input"
                          onChange={e => {
                            const copy = [...profile.gallery];
                            copy[idx].file = e.target.files[0];
                            setProfile({ ...profile, gallery: copy });
                          }}
                        />
                      ) : (
                        <input
                          className="profile-input"
                          placeholder={g.type === 'video' ? 'Video link' : 'Image URL'}
                          value={g.url || ''}
                          onChange={e => {
                            const copy = [...profile.gallery];
                            copy[idx].url = e.target.value;
                            setProfile({ ...profile, gallery: copy });
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  className="profile-btn-secondary w-100"
                  onClick={() => {
                    const copy = [...(profile.gallery || [])];
                    copy.push({ type: 'image', uploadMethod: 'link', url: '', file: null });
                    setProfile({ ...profile, gallery: copy });
                  }}
                >
                  + Add Media Item
                </button>
              </div>


            </section>
          )}

          {step === 2 && (
            <section className="slide">
              <h3 className="section-title">Achievements & Awards</h3>
              <p className="section-subtitle">
                Highlight recognitions, certifications, or milestones achieved by your organization.
              </p>

              <div className="awards-list mt-4">
                {(profile.awards || []).map((a, idx) => (
                  <div key={idx} className="award-card">

                    <div className="award-header">
                      <h5>Award #{idx + 1}</h5>
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => {
                          const copy = [...profile.awards];
                          copy.splice(idx, 1);
                          setProfile({ ...profile, awards: copy });
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="form-row two-col">
                      <div className="form-group">
                        <label><span>Award Title</span></label>
                        <input
                          className="form-input"
                          placeholder="e.g. Best Travel Partner"
                          value={a.title || ""}
                          onChange={e => {
                            const copy = [...profile.awards];
                            copy[idx].title = e.target.value;
                            setProfile({ ...profile, awards: copy });
                          }}
                        />
                      </div>

                      <div className="form-group">
                        <label><span>Year</span></label>
                        <input
                          type="number"
                          className="form-input"
                          placeholder="e.g. 2024"
                          value={a.year || ""}
                          onChange={e => {
                            const copy = [...profile.awards];
                            copy[idx].year = e.target.value;
                            setProfile({ ...profile, awards: copy });
                          }}
                        />
                      </div>
                    </div>

                    <div className="form-group mt-2">
                      <label><span>Award Image URL</span></label>
                      <input
                        className="form-input"
                        placeholder="https://example.com/award-image.jpg"
                        value={a.image || ""}
                        onChange={e => {
                          const copy = [...profile.awards];
                          copy[idx].image = e.target.value;
                          setProfile({ ...profile, awards: copy });
                        }}
                      />
                    </div>

                    <div className="form-group mt-2">
                      <label><span>Additional Information</span></label>
                      <textarea
                        className="form-textarea"
                        rows={3}
                        placeholder="Short description, issuing authority, or achievement details"
                        value={a.info || ""}
                        onChange={e => {
                          const copy = [...profile.awards];
                          copy[idx].info = e.target.value;
                          setProfile({ ...profile, awards: copy });
                        }}
                      />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-primary mt-3"
                  onClick={() => {
                    const copy = [...(profile.awards || [])];
                    copy.push({ title: "", year: "", image: "", info: "" });
                    setProfile({ ...profile, awards: copy });
                  }}
                >
                  + Add New Award
                </button>
              </div>
            </section>
          )}


          {step === 3 && (
            <section className="slide">
              <h3 className="section-title">Blogs</h3>
              <p className="section-subtitle">
                Add articles, thought leadership posts, or company updates published by your organization.
              </p>

              <div className="blogs-list mt-4">
                {(profile.blogs || []).map((b, idx) => (
                  <div key={idx} className="profile-blog-card">

                    <div className="profile-blog-header">
                      <h5>Blog #{idx + 1}</h5>
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => {
                          const copy = [...profile.blogs];
                          copy.splice(idx, 1);
                          setProfile({ ...profile, blogs: copy });
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="form-group">
                      <label>Blog Title</label>
                      <input
                        className="form-input"
                        placeholder="e.g. Top Travel Trends for 2025"
                        value={b.title || ""}
                        onChange={e => {
                          const copy = [...profile.blogs];
                          copy[idx].title = e.target.value;
                          setProfile({ ...profile, blogs: copy });
                        }}
                      />
                    </div>

                    <div className="form-row two-col mt-2">
                      <div className="form-group">
                        <label>Blog Image URL</label>
                        <input
                          className="form-input"
                          placeholder="https://example.com/blog-image.jpg"
                          value={b.image || ""}
                          onChange={e => {
                            const copy = [...profile.blogs];
                            copy[idx].image = e.target.value;
                            setProfile({ ...profile, blogs: copy });
                          }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Blog PDF (Optional)</label>
                        <input
                          className="form-input"
                          placeholder="https://example.com/blog.pdf"
                          value={b.pdf || ""}
                          onChange={e => {
                            const copy = [...profile.blogs];
                            copy[idx].pdf = e.target.value;
                            setProfile({ ...profile, blogs: copy });
                          }}
                        />
                      </div>
                    </div>

                    <div className="form-group mt-2">
                      <label>External Blog Link</label>
                      <input
                        className="form-input"
                        placeholder="https://yourwebsite.com/blog-post"
                        value={b.link || ""}
                        onChange={e => {
                          const copy = [...profile.blogs];
                          copy[idx].link = e.target.value;
                          setProfile({ ...profile, blogs: copy });
                        }}
                      />
                    </div>

                    <div className="form-group mt-2">
                      <label>Blog Content / Summary</label>
                      <textarea
                        className="form-textarea"
                        rows={4}
                        placeholder="Short summary or main content of the blog"
                        value={b.content || ""}
                        onChange={e => {
                          const copy = [...profile.blogs];
                          copy[idx].content = e.target.value;
                          setProfile({ ...profile, blogs: copy });
                        }}
                      />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-primary mt-3"
                  onClick={() => {
                    const copy = [...(profile.blogs || [])];
                    copy.push({ title: "", image: "", pdf: "", link: "", content: "" });
                    setProfile({ ...profile, blogs: copy });
                  }}
                >
                  + Add New Blog
                </button>
              </div>
            </section>
          )}


          {step === 4 && (
            <section className="slide">
              <h3 className="section-title">Membership Details</h3>
              <p className="section-subtitle">
                Provide details of professional memberships, associations, or industry bodies.
              </p>

              <div className="membership-list mt-4">
                {(profile.membershipDetails || []).map((m, idx) => (
                  <div key={idx} className="profile-membership-card">

                    <div className="profile-membership-header">
                      <h5>Membership #{idx + 1}</h5>
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => {
                          const copy = [...profile.membershipDetails];
                          copy.splice(idx, 1);
                          setProfile({ ...profile, membershipDetails: copy });
                        }}
                      >
                        Remove
                      </button>
                    </div>

                    <div className="form-row two-col">
                      <div className="form-group">
                        <label>Name of Society / Association</label>
                        <input
                          className="form-input"
                          placeholder="e.g. Indian Hospitality Network"
                          value={m.society || ""}
                          onChange={e => {
                            const copy = [...profile.membershipDetails];
                            copy[idx].society = e.target.value;
                            setProfile({ ...profile, membershipDetails: copy });
                          }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Nature of Membership</label>
                        <input
                          className="form-input"
                          placeholder="e.g. Corporate Member, Associate"
                          value={m.nature || ""}
                          onChange={e => {
                            const copy = [...profile.membershipDetails];
                            copy[idx].nature = e.target.value;
                            setProfile({ ...profile, membershipDetails: copy });
                          }}
                        />
                      </div>
                    </div>

                    <div className="form-group mt-2">
                      <label>Membership Plan</label>
                      <select
                        className="form-select"
                        value={m.plan || ""}
                        onChange={e => {
                          if (e.target.value === "choose") {
                            window.open(
                              "https://indianhospitalitynetwork.com",
                              "_blank",
                              "noopener,noreferrer"
                            );
                          } else {
                            const copy = [...profile.membershipDetails];
                            copy[idx].plan = e.target.value;
                            setProfile({ ...profile, membershipDetails: copy });
                          }
                        }}
                      >
                        <option value="">Select a Plan</option>
                        <option value="India">India</option>
                        <option value="India+">India +</option>
                      </select>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  className="btn btn-primary mt-3"
                  onClick={() => {
                    const copy = [...(profile.membershipDetails || [])];
                    copy.push({ society: "", nature: "", plan: "" });
                    setProfile({ ...profile, membershipDetails: copy });
                  }}
                >
                  + Add Membership
                </button>
              </div>
            </section>
          )}


          {step === 5 && (
            <section className="slide">
              <h3>Business Details</h3>
              <label className="mt-4"><span>PAN Card Number</span>
                <input
                  value={profile.panCard || ''}
                  onChange={e => setProfile({ ...profile, panCard: e.target.value })}
                  placeholder="Enter PAN number"
                />
              </label>
              <label className="mt-4"><span>Upload PAN Card File</span>
                <input
                  type="file"
                  onChange={e => setPanFile(e.target.files[0])} // store in new state
                />
              </label>
              <label className="mt-4"><span>Aadhar Card Number</span>
                <input
                  value={profile.aadharCard || ''}
                  onChange={e => setProfile({ ...profile, aadharCard: e.target.value })}
                  placeholder="Enter Aadhar number"
                /></label>
              <label className="mt-4"><span>Upload Aadhar Card File</span>
                <input
                  type="file"
                  onChange={e => setAadharFile(e.target.files[0])} // store in new state
                />
              </label>

              <label className="mt-4"><span>GST No.</span>
                <input value={profile.gst || ''} onChange={e => setProfile({ ...profile, gst: e.target.value })} />
              </label>
              <label className="mt-4"><span>Upload GST Certificate</span>
                <input
                  type="file"
                  onChange={e => setGstFile(e.target.files[0])} // store in new state
                />
              </label>
              <div className="security-note mt-4">
                <p><strong>Note:</strong> This information is secure. We will not show this information in your profile.</p>
              </div>
            </section>
          )}

          <div className="slide-actions">
            {step > 0 && <button type="button" className="btn" onClick={() => setStep(s => Math.max(0, s - 1))}>Back</button>}
            {step < 5 && <button type="button" className="btn primary" onClick={() => setStep(s => Math.min(5, s + 1))}>Next</button>}
            {step === 5 && <div className="actions"><button className="btn primary" type="submit">Save</button> <button type="button" className="btn" onClick={() => { localStorage.removeItem('ihn_token'); navigate('/login'); }}>Logout</button></div>}
          </div>
        </form>

      </div>
    </div>
  );
}
